import { FilterAssets } from '@terminal/components/filters/components/filter-assets/FilterAssets';
import { HorizonSelector } from '@terminal/components/horizon-selector/HorizonSelector';
import { AddAssets } from '@terminal/components/send-all-assets-to/add-assets/AddAssets';
import { SendAllAssetsTo } from '@terminal/components/send-all-assets-to/SendAllAssetsTo';
import { useFilterActions } from '@terminal/hooks/use-filter-actions/useFilterActions';
import { ModalTypes, useModal } from '@terminal/hooks/use-modal/useModal';
import { usePublicLists } from '@terminal/react-query-hooks/usePublicLists';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { useTranslation } from 'react-i18next';

import { useScenarioStore } from '../../hooks/use-scenario-store/useScenarioStore';
import styles from './ScenarioToolbar.module.scss';

export interface ScenarioToolbarProps {
  activeHorizon: string;
  entityTags: string[];
  hasData: boolean;
  horizonLabels: string[];
  horizonKeys: string[];
  setActiveHorizon: (horizon: string) => void;
}

export const ScenarioToolbar = ({
  activeHorizon,
  entityTags,
  hasData,
  horizonLabels,
  horizonKeys,
  setActiveHorizon,
}: ScenarioToolbarProps) => {
  const { t } = useTranslation(['scenario', 'screener']);
  const { data: publicLists } = usePublicLists();
  const { open } = useModal(ModalTypes.AddAssets);

  const { assetFilterGroup } = useScenarioStore(state => ({
    assetFilterGroup: state.filterGroups.ASSET_FILTERS,
  }));
  const { addFilterOption, resetFilterOptions } = useFilterActions({
    store: useScenarioStore,
    group: 'ASSET_FILTERS',
  });

  const setActiveHorizonIndex = (index: number) =>
    setActiveHorizon(horizonKeys[index]);

  if (!assetFilterGroup.activeFilters.length) {
    return null;
  }

  const activeFilter = assetFilterGroup.activeFilters?.[0];
  const activeFilterOption = assetFilterGroup.activeFilterOptions?.[0];

  const filterProps = {
    filter: activeFilter,
    filterOptions: activeFilterOption?.options ?? [],
    publicLists: publicLists ?? [],
    addFilterOption,
    resetFilterOptions,
    source: POSTHOG_TRACKING_SOURCES.SCENARIO,
  };

  const hasEntities = !!entityTags.length;
  const activeHorizonIndex = horizonKeys.indexOf(activeHorizon);
  return (
    <div className={styles.scenarioToolbarRoot} data-testid="scenario-toolbar">
      <div className={styles.leftWrapper}>
        <FilterAssets {...filterProps} />
        {hasEntities && (
          <div className={styles.assetCounter}>
            <span className={styles.assetCounterLabel}>
              {t('scenario:assetsSelected', {
                count: entityTags.length,
              })}
            </span>
          </div>
        )}
        <AddAssets onClick={open} />
      </div>
      <div className={styles.rightWrapper}>
        {hasData && hasEntities && (
          <HorizonSelector
            className={styles.styledHorizonSelector}
            horizonLabels={horizonLabels}
            onClick={setActiveHorizonIndex}
            activeHorizonIndex={activeHorizonIndex}
          />
        )}
        <SendAllAssetsTo entityTags={entityTags} source={'scenario'} />
      </div>
    </div>
  );
};
